import { Col, Flex, Tab, TabGroup } from '@appliedsystems/applied-design-system';
import { PaymentMethod } from '@appliedsystems/payments-core';
import React, { useEffect, useState } from 'react';
import achIcon from '../../assets/icons/ach.svg';
import creditCardIcon from '../../assets/icons/credit-card.svg';
import { currencyMap } from '../../constants/constants';
import { usePaymentsTranslation } from '../../hooks/usePaymentsTranslation';
import { Locale } from '../../store/Locale';
import { getAmountWithFees } from '../../util/getAmountWithFees';
import { AvailablePaymentMethod, HppData } from '../HostedPaymentPageContainer/types';
import { useHppDataStore } from '../HostedPaymentPageContainer/useHppData';
import { useHppSessionStore } from '../HostedPaymentPageContainer/useHppSession';
import { PaymentOptionCard } from '../PaymentOptionCard/PaymentOptionCard';
import { PaymentOptionRadioButton } from '../PaymentOptionRadioButton/PaymentOptionRadioButton';
import classes from './PaymentMethodSelection.module.scss';

export type PaymentMethodDetails = {
  [key in AvailablePaymentMethod]: {
    icon: string;
    title: string;
    tooltip?: string;
  };
};

type Props = {
  pfDisabled?: boolean;
  isDownPayment?: boolean;
  onPaymentMethodSelected: (paymentDetails: Pick<HppData, 'paymentMethod' | 'paymentFee'>) => void;
};

export const availablePaymentMethods: AvailablePaymentMethod[] = [
  PaymentMethod.Card,
  PaymentMethod.Ach,
  PaymentMethod.Eft,
];

export const PaymentMethodSelection = ({
  isDownPayment: isInDownPaymentAccordion,
  onPaymentMethodSelected,
  pfDisabled,
}: Props): React.ReactElement => {
  const { t } = usePaymentsTranslation();
  const { hppData, paymentMethodConfig } = useHppDataStore();
  const { hppSession, getFeePercentOrAmount } = useHppSessionStore();
  const { locale } = Locale.useContainer();

  const [activeTab, setActiveTab] = useState<AvailablePaymentMethod | undefined>(hppData.paymentMethod);

  useEffect(() => {
    if (hppData.paymentMethod) {
      if (hppData.paymentMethod !== activeTab) {
        const paymentConfig = paymentMethodConfig[hppData.paymentMethod as AvailablePaymentMethod];
        onPaymentMethodSelected({
          paymentMethod: hppData.paymentMethod as AvailablePaymentMethod,
          paymentFee: paymentConfig.fee,
        });
      }
      setActiveTab(hppData.paymentMethod as AvailablePaymentMethod);
    }
  }, [activeTab, hppData.paymentMethod, onPaymentMethodSelected, paymentMethodConfig]);

  const weHavePfQuote = hppData.pf?.quote?.quoteId;

  const paymentMethods: PaymentMethodDetails = {
    [PaymentMethod.Ach]: {
      icon: achIcon,
      title: t('ACH_DIRECT_DEBIT'),
      tooltip: t('ACH_DIRECT_DEBIT_TOOLTIP'),
    },
    [PaymentMethod.Card]: {
      icon: creditCardIcon,
      title: t('CREDIT_CARD'),
    },
    [PaymentMethod.Eft]: {
      icon: achIcon,
      title: t('EFT_DIRECT_DEBIT'),
    },
  };

  if ((weHavePfQuote && !isInDownPaymentAccordion) || pfDisabled) {
    return (
      <PaymentOptionRadioButton
        availablePaymentMethods={availablePaymentMethods}
        paymentMethods={paymentMethods}
        onPaymentMethodSelected={onPaymentMethodSelected}
      />
    );
  }

  return (
    <Flex className={`${classes.container} ${classes.customPaymentMethodTabs}`}>
      {/* Show checkout B if tenant is in the list AND its not a PF payment */}
      {hppData.checkoutBEnabled ? (
        <TabGroup
          activeTab={activeTab}
          onActiveTabChange={(tabKey) => {
            if (hppData.paymentMethod !== tabKey) {
              const paymentConfig = paymentMethodConfig[tabKey as AvailablePaymentMethod];
              onPaymentMethodSelected({
                paymentMethod: tabKey as AvailablePaymentMethod,
                paymentFee: paymentConfig.fee,
              });
            }
            setActiveTab(tabKey as AvailablePaymentMethod);
          }}
        >
          {availablePaymentMethods
            .filter((method) => hppSession?.allowedPaymentMethods.includes(method) ?? true)
            .map((paymentMethodKey) => {
              const paymentConfig = paymentMethodConfig[paymentMethodKey];

              const { title } = paymentMethods[paymentMethodKey];
              const subtotal = hppData.paymentAmount;

              const { paymentFee } = getAmountWithFees(
                paymentMethodKey,
                Number(subtotal),
                paymentConfig.fee,
                currencyMap[locale],
              );

              const feeText = getFeePercentOrAmount(paymentMethodKey, subtotal, paymentFee);

              return (
                <Tab title={title} tabKey={paymentMethodKey} key={paymentMethodKey} disabled={!paymentConfig.allowed}>
                  {t('PAYMENT_FEE_DETAILS', undefined, {
                    feeAmount: feeText,
                  } as any)}
                </Tab>
              );
            })}
        </TabGroup>
      ) : (
        availablePaymentMethods
          .filter((method) => hppSession?.allowedPaymentMethods.includes(method) ?? true)
          .map((paymentMethodKey) => {
            const paymentConfig = paymentMethodConfig[paymentMethodKey];

            if (!paymentConfig.allowed) return <Col key={paymentMethodKey}></Col>; // Not an allowed payment method

            const { icon, title, tooltip } = paymentMethods[paymentMethodKey];

            const subtotal =
              isInDownPaymentAccordion && hppData.pf
                ? BigInt(hppData.pf.pfSubtotal!) + BigInt(hppData.pf.quote?.retainedPayments ?? 0)
                : hppData.paymentAmount;

            const { paymentTotal, paymentFee } = getAmountWithFees(
              paymentMethodKey,
              Number(subtotal),
              paymentConfig.fee,
              currencyMap[locale],
            );

            return (
              <PaymentOptionCard
                pfDisabled={pfDisabled}
                key={paymentMethodKey}
                icon={icon}
                title={title}
                subtotal={Number(subtotal)}
                total={paymentTotal}
                feeText={getFeePercentOrAmount(paymentMethodKey, Number(subtotal), paymentFee)}
                isActiveCard={hppData.paymentMethod === paymentMethodKey}
                tooltip={tooltip}
                choosePaymentMethod={() => {
                  if (hppData.paymentMethod !== paymentMethodKey) {
                    onPaymentMethodSelected({
                      paymentMethod: paymentMethodKey,
                      paymentFee: paymentConfig.fee,
                    });
                  }
                }}
              />
            );
          })
      )}
    </Flex>
  );
};
